import { useRef, useState } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, className = "", as = "div" }) => {
    const reduce = useReducedMotion();
    const M = motion[as] || motion.div;
    return (
        <M
            initial={{ opacity: 0, y: reduce ? 0 : 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-15%" }}
            transition={{ duration: 0.7, delay, ease: EASE }}
            className={className}
        >
            {children}
        </M>
    );
};

export const ImageReveal = ({ children, className = "", delay = 0 }) => {
    const reduce = useReducedMotion();
    return (
        <motion.div
            initial={{
                opacity: 0,
                scale: reduce ? 1 : 1.06,
                clipPath: reduce ? "inset(0% 0% 0% 0%)" : "inset(10% 4% 10% 4%)",
            }}
            whileInView={{ opacity: 1, scale: 1, clipPath: "inset(0% 0% 0% 0%)" }}
            viewport={{ once: true, margin: "-15%" }}
            transition={{ duration: 0.9, delay, ease: EASE }}
            className={className}
        >
            {children}
        </motion.div>
    );
};

export const Parallax = ({ children, amount = 36, className = "" }) => {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [amount, -amount]);
    return (
        <div ref={ref} className={`relative ${className}`}>
            <motion.div style={reduce ? undefined : { y }} className="h-full">
                {children}
            </motion.div>
        </div>
    );
};

export const Magnetic = ({ children, strength = 5, className = "" }) => {
    const reduce = useReducedMotion();
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const ref = useRef(null);
    const onMove = (e) => {
        if (reduce) return;
        const r = ref.current.getBoundingClientRect();
        setOffset({
            x: ((e.clientX - r.left) / r.width - 0.5) * strength * 2,
            y: ((e.clientY - r.top) / r.height - 0.5) * strength * 2,
        });
    };
    return (
        <motion.div
            ref={ref}
            onMouseMove={onMove}
            onMouseLeave={() => setOffset({ x: 0, y: 0 })}
            animate={{ x: offset.x, y: offset.y }}
            transition={{ duration: 0.35, ease: EASE }}
            className={`inline-block ${className}`}
        >
            {children}
        </motion.div>
    );
};

export { EASE };
